import { type EmployeeLevel, type FundingRound, type Prisma } from "@prisma/client";
import { type AsyncReturnType } from "type-fest";
import { type AppContext } from "~/lib/context";
import { getRequiredUser } from "~/lib/getRequiredUser";
import { isNil } from "~/lib/lodash";
import { type CreatePresetInput } from "~/pages/api/filters-preset/create-preset";
import { type MarketOrCompareInput } from "~/pages/api/get-market-data-stats";
import { splitCustomJobIds } from "~/services/custom-jobs/splitCustomJobIds";
import { getHeadcountPayload, getIndustryPayload } from "~/services/marketDataSearch";
export const createFiltersPreset = async (ctx: AppContext, input: CreatePresetInput) => {
  const user = getRequiredUser(ctx);
  const canAccessFiguresAiV2 = ctx.featureFlags.CAN_ACCESS_FIGURES_AI_V2;

  const industryPayloadInput: Pick<MarketOrCompareInput, "industryId" | "industriesIds"> = {
    industryId: input.market?.industryId ?? null,
    industriesIds: !isNil(input.market?.industriesIds) ? (input.market?.industriesIds as number[]) : [],
  };

  const labelsIds = input.market && input.market?.labelsIds?.length > 0 ? (input.market?.labelsIds as number[]) : [];
  const locationsIds =
    input.market && input.market?.locationsIds?.length > 0 ? (input.market?.locationsIds as number[]) : [];
  const mixedJobIds =
    input.market?.jobsIds && input.market?.jobsIds?.length > 0 ? (input.market?.jobsIds as string[]) : [];
  const { jobIds } = splitCustomJobIds(mixedJobIds);

  const marketFiltersPayload: Prisma.MarketFiltersCreateInput = {
    ...getIndustryPayload(industryPayloadInput, { canAccessFiguresAiV2 }),
    ...(input.market && getHeadcountPayload(input.market, { canAccessFiguresAiV2 })),
    ...(input.market?.fundingRounds && { fundingRounds: { set: input.market.fundingRounds as FundingRound[] } }),
    labels: { connect: labelsIds.map((id) => ({ id })) },
    locations: { connect: locationsIds.map((id) => ({ id })) },
  };

  const inputPayload = {
    levels: (input.levels as EmployeeLevel[]) ?? [],
    percentiles: input.percentiles,
    mergedAdvancedLevels: input.mergeAdvancedLevels ?? false,
  };

  const filtersPresetPayload: Prisma.FiltersPresetCreateInput = {
    name: input.name,
    user: { connect: { id: user.id } },
    company: { connect: { id: user.companyId } },
    jobs: { connect: jobIds.map((id) => ({ id })) },
    input: inputPayload,
    marketFilters: {
      create: marketFiltersPayload,
    },
  };

  return ctx.prisma.filtersPreset.create({
    data: filtersPresetPayload,
  });
};

export type CreateFiltersPresetResult = AsyncReturnType<typeof createFiltersPreset>;
